import { getImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { propTypesDataFeaturePresentationHomepage } from './FeaturePresentationHomepage.proptypes';

import Picture from '../../../../ui/Atoms/Picture';
import Text, { TextAppearance } from '../../../../ui/Atoms/Text';

import { useSize } from '../../../hooks/useSize';
import { getStaticUrl } from '../../../utils/getStaticUrl';

import Button, { ButtonAppearance } from '../../../../ui/Atoms/Button';
import { Breakpoint } from '../../../constants/Styles';
import { addTrackingParamsToUrl } from '../../../utils/trackingParams';
import * as S from './FeaturePresentationHomepage.styles';

const FeatureCard = ({ feature }) => {
  const {
    feature_link: { url, target } = {},
    feature_name: featureName,
    feature_picture: { localFile, alt } = {},
  } = feature || '';

  const imageProps = {
    imageObject: getImage(localFile),
    imageUrl: getStaticUrl(localFile?.publicURL),
    altText: alt,
    height: '25px',
    width: '25px',
  };

  if (!imageProps || !featureName) {
    return null;
  }

  return (
    <S.FeatureCardWrapper href={url} target={target}>
      <Picture {...imageProps} />
      <S.FeatureName>{featureName}</S.FeatureName>
    </S.FeatureCardWrapper>
  );
};

export const FeaturePresentationHomepageSlice = ({ slice }) => {
  const screenWidth = useSize().width;
  if (!slice) {
    return null;
  }

  let pictureWidth = 500;

  if (screenWidth <= Number(Breakpoint.L.slice(0, -2))) {
    pictureWidth = 467;
  }
  if (screenWidth <= Number(Breakpoint.M.slice(0, -2))) {
    pictureWidth = 369;
  }
  if (screenWidth <= Number(Breakpoint.S.slice(0, -2))) {
    pictureWidth = 234;
  }

  const list = slice.items || [];
  const data = slice.primary || {};
  const {
    feature_presentation_slice_id: id,
    is_reverse: isReverse,
    feature_presentation_title: title,
    feature_presentation_intro: { html: intro } = {},
    feature_presentation_cta_label: ctaLabel,
    feature_presentation_cta_link: { url, target },
    feature_presentation_cta_datatrack: datatrack,
  } = data || '';

  const [link, setLink] = useState(url);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setLink(addTrackingParamsToUrl(url));
    }, 500);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const handleCookieChange = () => {
      setLink(addTrackingParamsToUrl(url));
    };

    document.addEventListener('cookiechange', handleCookieChange);

    return () => {
      document.removeEventListener('cookiechange', handleCookieChange);
    };
  }, []);

  return (
    <S.FeaturePresentation id={id}>
      <S.Wrapper>
        <S.LeftContainer isReverse={isReverse}>
          {title && <S.Title>{title}</S.Title>}
          {intro && <Text type={TextAppearance.NORMAL}>{intro}</Text>}
          {ctaLabel && url && (
            <Button
              appearance={ButtonAppearance.OUTLINE}
              dataTrackAttribute={datatrack}
              link={link}
            >
              {ctaLabel}
            </Button>
          )}
        </S.LeftContainer>
        <S.RightContainer isReverse={isReverse}>
          {list.length > 2 && (
            <S.ListContainer>
              {list?.map((feature, index) => (
                <FeatureCard key={`feature-card-${index}`} feature={feature} />
              ))}
            </S.ListContainer>
          )}
        </S.RightContainer>
      </S.Wrapper>
    </S.FeaturePresentation>
  );
};

FeaturePresentationHomepageSlice.propTypes = {
  /**
   * Content of the QuickPresentation slice.
   */
  slice: PropTypes.shape(propTypesDataFeaturePresentationHomepage),
};
